import React, { useState } from 'react';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import _ from 'lodash';
import { BookingType, FilterType, ShiftSlotStatus } from 'utilities/enum-utils';
import FoundWorkerRow from 'views/bookings-v2/components/FoundWorkerRow';
import { FilterSection } from 'common-components/filter';
import { Badge, Button, Collapse, Grid, Group, Paper, Stack, Switch, ThemeIcon, UnstyledButton } from '@good/ui/core';
import { ChevronDown, ChevronUp } from '@good/icons';
import { Address } from '@good/schemas';
import { IFilter } from 'interfaces/filter-interfaces';
import { Box, Text, Title } from '@good/ui/core';
import { AssignSessionButton } from './assign-session-button';
import { BookingAssignWorkerActiveFilters, Worker } from './types';
import SelectWorkerSearch from 'views/bookings-v2/components/select-worker-search';

const availableFilters = [
  FilterType.QUALIFICATIONS,
  FilterType.RELIGIONS,
  FilterType.GENDER,
  FilterType.SKILLS,
  FilterType.LANGUAGES,
  FilterType.INTEREST,
];

interface AssignWorkerStep2Props {
  checkForConflict: () => void;
  onAssignWorker: (shiftSlotStatus: ShiftSlotStatus) => void;
  onChangeFilter: (filterKey: keyof BookingAssignWorkerActiveFilters, value: boolean) => void;
  onClickAssign: (shiftSlotStatus: ShiftSlotStatus) => void;
  onOpenAddTimesheet: () => void;
  onSelectWorker: (worker: Worker | null) => void;
  activeFilters: BookingAssignWorkerActiveFilters;
  address: Address | undefined;
  bookingId: string | undefined;
  bookingType: BookingType | undefined;
  customerUserIds: string[] | undefined;
  endDateTime: Date | undefined;
  filteredWorkers: Worker[];
  isAssignedTeamMemberConfirmed: boolean | undefined;
  isBookingCancelled?: boolean;
  isEditingBooking: boolean | undefined;
  isLoadingFoundWorkers: boolean;
  isRecurring: boolean | undefined;
  listFilters: IFilter[];
  onChangeListFilter: (filters: IFilter[]) => void;
  selectedWorker: Worker | null;
  serviceId: string;
  startDateTime: Date | undefined;
}

export const AssignWorkerStep2 = ({
  activeFilters,
  address,
  bookingId,
  bookingType,
  customerUserIds,
  endDateTime,
  filteredWorkers,
  isAssignedTeamMemberConfirmed,
  isBookingCancelled,
  isEditingBooking,
  isLoadingFoundWorkers,
  isRecurring,
  listFilters,
  onChangeListFilter,
  serviceId,
  startDateTime,
  selectedWorker,
  onAssignWorker,
  onChangeFilter,
  onSelectWorker,
  onOpenAddTimesheet,
  checkForConflict,
  onClickAssign,
}: AssignWorkerStep2Props) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');

  const onCloseSearchModal = () => {
    setIsSearchOpen(false);
  };

  return (
    <>
      <Text c='dimmed' pb='xs'>
        {isAssignedTeamMemberConfirmed && ' Once assigned, the team member will become confirmed.'}
      </Text>
      <Grid className='width-full' gutter='xs'>
        <Grid.Col span={12}>
          <div className=''>
            <Title order={4}>Assign team member</Title>
            <UnstyledButton w='100%' onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
              <Group justify='space-between'>
                <Group gap={6}>
                  <ThemeIcon variant='transparent' size='sm'>
                    {filtersCollapsed ? <ChevronDown /> : <ChevronUp />}
                  </ThemeIcon>
                  <Text size='sm' c='ocean' fw={600}>
                    {filtersCollapsed ? 'Show' : 'Hide'} filters
                  </Text>
                </Group>
                <Badge variant='default' size='md'>
                  {filteredWorkers ? filteredWorkers.length : 0}
                </Badge>
              </Group>
            </UnstyledButton>
            <Collapse in={!filtersCollapsed}>
              <Text size='sm' c='dimmed'>
                Show team members who...
              </Text>
              <Stack pt='xs' gap='xs'>
                <Switch
                  checked={activeFilters?.isAvailable}
                  onChange={(e) => onChangeFilter('isAvailable', e.currentTarget.checked)}
                  label='Available to work this booking'
                />
                <Switch
                  checked={activeFilters?.isSkillMatched}
                  onChange={(e) => onChangeFilter('isSkillMatched', e.currentTarget.checked)}
                  label='Skill matched for the customer(s) care needs'
                />
                <Switch
                  checked={activeFilters?.previouslyUsed}
                  onChange={(e) => onChangeFilter('previouslyUsed', e.currentTarget.checked)}
                  label='Worked with the customer(s) before'
                />
                <Switch
                  checked={activeFilters?.isCustomerPreferred}
                  label='Preferred team members by the customer'
                  onChange={(e) => onChangeFilter('isCustomerPreferred', e.currentTarget.checked)}
                />
                <FilterSection
                  availableFilters={availableFilters}
                  filters={listFilters}
                  onChangeFilter={onChangeListFilter}
                  displayTimezone={null}
                  usePortal={false}
                  verticalDisplay={true}
                />
              </Stack>
            </Collapse>
          </div>
        </Grid.Col>
        <Grid.Col span={12}>
          <div className=''>
            <SelectWorkerSearch
              isOpen={isSearchOpen}
              selectedWorker={selectedWorker}
              bookingId={bookingId}
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              address={address}
              customerUserIds={customerUserIds}
              serviceId={serviceId}
              onCloseSearchModal={onCloseSearchModal}
              selectWorker={onSelectWorker}
              onAssignWorker={onAssignWorker}
              isBookingCancelled={isBookingCancelled}
              searchInputValue={searchInputValue}
              onChangeSearchInputValue={setSearchInputValue}
            />
            {!searchInputValue && (
              <Paper withBorder mt='xs'>
                <Box mih={300} mah={400} style={{ overflow: 'auto' }}>
                  {isLoadingFoundWorkers && <SpinningLoader size={100} message={'Loading'} />}
                  {_.isEmpty(filteredWorkers) && !isLoadingFoundWorkers && (
                    <div className='mt-x2-large text-align-center' style={{ margin: 'auto' }}>
                      <Text fw={600}>No Team member found</Text>
                      <br />
                      <Text>Select a filter or find Team member to select.</Text>
                    </div>
                  )}
                  <Box>
                    {filteredWorkers && filteredWorkers.length > 0 && !isLoadingFoundWorkers && (
                      <table className='width-full'>
                        <tbody>
                          {filteredWorkers.map((worker) => {
                            return (
                              <FoundWorkerRow
                                worker={worker}
                                key={worker.supportWorkerId}
                                isSelected={selectedWorker && worker.supportWorkerId === selectedWorker.supportWorkerId}
                                isSearch={false}
                                selectWorker={onSelectWorker}
                              />
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {bookingType === BookingType.ACTIVITY_RECORD ? (
              <div className='flex-row justify-end mt-large'>
                <Button
                  disabled={!selectedWorker}
                  onClick={
                    !isEditingBooking ? () => onAssignWorker(ShiftSlotStatus.CONFIRMED) : () => onOpenAddTimesheet()
                  }
                  size='large'
                  className='ml-large'
                >
                  Assign Team member
                </Button>
              </div>
            ) : (
              <div className='flex-row justify-end mt-large'>
                {!isEditingBooking && isRecurring ? (
                  <Button
                    disabled={!selectedWorker}
                    onClick={() => checkForConflict()}
                    size='large'
                    className='ml-large'
                  >
                    Assign Team member
                  </Button>
                ) : (
                  <AssignSessionButton
                    selectedWorker={selectedWorker}
                    onAssignWorker={(shiftSlotStatus: ShiftSlotStatus) => onClickAssign(shiftSlotStatus)}
                    isBookingCancelled={isBookingCancelled}
                  />
                )}
              </div>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </>
  );
};
